import React, { useContext, useState } from 'react';
import { NavLink, Outlet, Navigate } from 'react-router-dom';
import { Container, Row, Col, Nav, Spinner, Button, Offcanvas } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';

const roleRank = { customer: 0, editor: 1, admin: 2, superadmin: 3 };

const ADMIN_NAV_ITEMS = [
  { path: '/admin', label: 'Dashboard', icon: '📊', minRole: 'admin', end: true },
  { path: '/admin/products', label: 'Products', icon: '🛍️', minRole: 'editor' },
  { path: '/admin/product-order', label: 'Product Order', icon: '↕️', minRole: 'editor' },
  { path: '/admin/categories', label: 'Categories', icon: '🏷️', minRole: 'admin' },
  { path: '/admin/orders', label: 'Orders', icon: '📦', minRole: 'admin' },
  { path: '/admin/custom-orders', label: 'Custom Requests', icon: '💬', minRole: 'admin' },
  { path: '/admin/reviews', label: 'Reviews', icon: '⭐', minRole: 'admin' },
  { path: '/admin/promos', label: 'Promo Codes', icon: '🎟️', minRole: 'superadmin' },
  { path: '/admin/cms', label: 'Homepage CMS', icon: '🎨', minRole: 'admin' },
  { path: '/admin/about', label: 'About Us', icon: '👥', minRole: 'admin' },
  { path: '/admin/uploads', label: 'Uploads', icon: '🖼️', minRole: 'editor' },
  { path: '/admin/seller-applications', label: 'Seller Applications', icon: '🧵', minRole: 'admin' },
  { path: '/admin/users', label: 'Users', icon: '🛡️', minRole: 'superadmin' },
  { path: '/admin/settings', label: 'Settings', icon: '⚙️', minRole: 'superadmin' },
];

export default function AdminLayout() {
  const { user, loading } = useContext(AuthContext);
  const [showDrawer, setShowDrawer] = useState(false);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5 min-vh-50" role="status">
        <Spinner animation="border" variant="primary" />
        <span className="ms-2 text-muted">Loading admin portal…</span>
      </div>
    );
  }

  const userRole = String(user?.role || '').toLowerCase();
  const userRank = roleRank[userRole] || 0;

  if (userRank < 1) {
    return <Navigate to="/shop" replace />;
  }

  const visibleNavItems = ADMIN_NAV_ITEMS.filter(
    (item) => userRank >= (roleRank[item.minRole] || 0)
  );

  const handleCloseDrawer = () => setShowDrawer(false);
  const handleToggleDrawer = () => setShowDrawer((prev) => !prev);

  const renderNavLinks = () => (
    <Nav className="flex-column gap-1 admin-sidebar-nav">
      {visibleNavItems.map((item) => (
        <Nav.Item key={item.path}>
          <NavLink
            to={item.path}
            end={item.end}
            className={({ isActive }) =>
              `nav-link admin-nav-link d-flex align-items-center gap-2 px-3 py-2 rounded ${
                isActive ? 'active fw-bold' : ''
              }`
            }
            onClick={handleCloseDrawer}
          >
            <span className="admin-nav-icon">{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        </Nav.Item>
      ))}
    </Nav>
  );

  return (
    <div className="admin-shell d-flex flex-column flex-grow-1">
      {/* Mobile / Tablet Header Bar */}
      <div className="d-md-none border-bottom bg-body-tertiary px-3 py-2 d-flex justify-content-between align-items-center sticky-top admin-mobile-bar">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleToggleDrawer}
            aria-label="Toggle admin navigation menu"
            className="px-2 py-1"
          >
            ☰ Menu
          </Button>
          <span className="fw-semibold small text-uppercase text-muted">Admin Panel</span>
        </div>
        <span className="badge bg-primary-subtle text-primary border border-primary-subtle text-capitalize">
          {userRole}
        </span>
      </div>

      {/* Offcanvas Drawer for Mobile / Tablet */}
      <Offcanvas show={showDrawer} onHide={handleCloseDrawer} placement="start" className="admin-offcanvas">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold fs-5">Hunar Admin</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-3">
          <div className="mb-3 px-2 py-1 bg-body-tertiary rounded small">
            <span className="text-muted">Signed in as </span>
            <strong className="d-block text-truncate">{user?.email}</strong>
          </div>
          {renderNavLinks()}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Desktop Main Layout */}
      <Container fluid className="flex-grow-1 p-0">
        <Row className="g-0">
          {/* Desktop Left Sidebar */}
          <Col md={3} lg={2} className="d-none d-md-block border-end bg-body-tertiary admin-sidebar p-3">
            <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom">
              <div>
                <h5 className="m-0 font-weight-bold">Hunar Admin</h5>
                <span className="small text-muted text-capitalize">{userRole} view</span>
              </div>
            </div>
            {renderNavLinks()}
          </Col>

          {/* Main Content Area */}
          <Col md={9} lg={10} className="p-3 p-md-4 admin-content-area">
            <Outlet />
          </Col>
        </Row>
      </Container>
    </div>
  );
}
